/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { apiClient } from '@lib/api-client';
import { queryClient } from '@lib/react-query';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';

// I18n
import { useTranslation } from 'react-i18next';

// Types
import { IApiError, TParams } from '@global-types/index';
import { TBodyApi } from 'shared/types';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */

type TApiResponse = {
	sellPrice_europertco2eq: number;
	costPrice_europertco2eq: number;
	entity: string;
	name: string;
	location: {
		countryCode: string;
		place: string;
	};
	unit: string;
	technology: string;
	active: boolean;
	logo: string | null;
	cover: string;
	publicSpecs: string[];
	quantity: {
		total_aco2: number;
		held_aco2: number;
		burnt_aco2: number;
		remaining_aco2: number;
		unprocessed_gco2eq: number;
	};
	organization: string;
	createdAt: string;
	updatedAt: string;
	description: string;
	id: string;
};

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const updateMeasurabilityProject = ({
	data,
	id,
}: {
	data: TBodyApi;
	id: string;
}): Promise<TApiResponse> => {
	return apiClient.patch(`/measurabilities/${id}`, data);
};

export const useUpdateMeasurabilityProject = (
	page: number,
	size: number,
	queryParams: TParams,
	sortParams: string
) => {
	const { t } = useTranslation();
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: updateMeasurabilityProject,

		onSuccess: async () => {
			notifications.show({
				title: t('global.success'),
				message: t(
					'measurabilityProjects.notifications.measurabilityProjectSuccessfullyUpdated'
				),
				color: 'var(--mantine-color-igluGreen-9)',
			});

			queryClient.invalidateQueries({
				queryKey: [
					'databases-measurabilities',
					{
						page: page,
						size: size,
						queryParams: queryParams,
						sortParams: sortParams,
					},
				],
			});
		},
		onError: (error: IApiError) => {
			notifications.show({
				title: t('global.error'),
				message: t(`api.${error.type}`),
				color: 'var(--mantine-color-igluRed-9)',
			});
		},
	});

	return { mutate, isLoading, isSuccess };
};
